/**
 * Live meeting insights types for the Zoom in-meeting app.
 * Ported from src/core/types/liveMeetingInsights.ts and dependencies.
 */

export type InternalAgendaLifecycleStatus =
  | 'not_started'
  | 'in_progress'
  | 'covered'
  | 'covered_weak_confidence'
  | 'at_risk';

export type InternalOperatorEvidenceChip =
  | 'decision_captured'
  | 'action_item_captured'
  | 'follow_up_captured'
  | 'topic_moved_on'
  | 'unresolved'
  | 'none';

export type InternalOperatorStatusReasonCode =
  | 'no_public_topic_link'
  | 'linked_topic_not_in_live_agenda'
  | 'linked_topic_not_reached'
  | 'linked_topic_active_without_decision'
  | 'linked_topic_has_decision'
  | 'linked_topic_has_action_or_follow_up_only'
  | 'agenda_drift_elevated'
  | 'linked_topic_advanced_without_decision';

export interface LiveMeetingInsightsLifecycleCounts {
  total: number;
  notStarted: number;
  inProgress: number;
  covered: number;
  coveredWeakConfidence: number;
  atRisk: number;
}

export type LiveMeetingInsightPriority = 'high' | 'medium' | 'low';

export interface LiveMeetingInsightActorV1 {
  userId: string | null;
  displayName: string | null;
}

export interface SharedTeamLiveMemberV1 {
  participantId: string;
  userId: string | null;
  displayName: string | null;
  role: 'owner' | 'member';
  isViewer: boolean;
}

export interface SharedTeamAgendaRowV1 {
  agendaItemId: string;
  title: string;
  isActive: boolean;
  lifecycleStatus: InternalAgendaLifecycleStatus;
  statusSummary: string;
  statusReasonCodes: InternalOperatorStatusReasonCode[];
  evidenceChips: InternalOperatorEvidenceChip[];
  decisionCount: number;
  actionItemCount: number;
  followUpCount: number;
  linkedInternalGoalCount: number;
  linkedInternalAtRiskCount: number;
}

export interface SharedTeamAttentionSignalV1 {
  signalId: string;
  code:
    | 'agenda_drift'
    | 'active_topic_unresolved'
    | 'topic_moved_without_decision'
    | 'shared_internal_goal_at_risk'
    | 'unanswered_question';
  severity: 'info' | 'warning';
  agendaItemId: string | null;
  message: string;
}

export interface SharedTeamQueueActionStateV1 {
  status: 'open' | 'acknowledged' | 'claimed' | 'resolved' | 'remind_later';
  actedBy: LiveMeetingInsightActorV1 | null;
  updatedAtMs: number | null;
  remindUntilMs: number | null;
}

export interface SharedTeamQueueItemV1 extends SharedTeamAttentionSignalV1 {
  priority: LiveMeetingInsightPriority;
  sourceKind: 'shared_team_signal';
  actionState: SharedTeamQueueActionStateV1;
}

export type SharedTeamQuestionParticipantScope = 'internal_team' | 'external_or_unknown';

export interface LiveMeetingInsightParticipantV1 extends LiveMeetingInsightActorV1 {
  participantId: string | null;
}

export type QuestionClassificationType =
  | 'clarification'
  | 'decision_seeking'
  | 'information_seeking'
  | 'challenge'
  | 'follow_up';

export interface SharedTeamTrackedQuestionV1 {
  questionId: string;
  questionText: string;
  agendaItemId: string | null;
  agendaItemTitle: string | null;
  askedAtMs: number;
  askedBy: LiveMeetingInsightParticipantV1;
  askedByScope: SharedTeamQuestionParticipantScope;
  status: 'open' | 'answered' | 'partially_answered';
  answeredAtMs: number | null;
  answeredBy: LiveMeetingInsightParticipantV1 | null;
  answeredByScope: SharedTeamQuestionParticipantScope | null;
  confidence: number | null;
  questionType: QuestionClassificationType | null;
  answerQuality: 'full' | 'partial' | 'tangential' | null;
  answerSummary: string | null;
  relatedQuestionId: string | null;
}

export interface SharedTeamQuestionSummaryV1 {
  total: number;
  open: number;
  answered: number;
  partiallyAnswered: number;
  answeredByInternalTeam: number;
  answeredByExternalOrUnknown: number;
}

export interface SharedTeamLiveMeetingViewV1 {
  visibility: 'shared_internal_team';
  internalTeamMembersOnCall: SharedTeamLiveMemberV1[];
  activeAgendaItemId: string | null;
  activeAgendaItemTitle: string | null;
  topicSummary: LiveMeetingInsightsLifecycleCounts;
  topicRows: SharedTeamAgendaRowV1[];
  attentionSignals: SharedTeamAttentionSignalV1[];
  sharedQueue: SharedTeamQueueItemV1[];
  questionSummary: SharedTeamQuestionSummaryV1;
  liveQuestions: SharedTeamTrackedQuestionV1[];
  privateGoalSummary: LiveMeetingInsightsLifecycleCounts | null;
}

export type PersonalLiveMeetingViewerRole =
  | 'meeting_owner'
  | 'internal_team_member'
  | 'internal_team_member_unlinked';

export type PersonalLiveInsightOriginKind = 'viewer_private_tip';

export type PersonalPrivateNotificationCode =
  | 'interrupt_warning'
  | 'interrupt_soft_mute'
  | 'interrupt_hard_mute'
  | 'tangent_nudge'
  | 'tangent_escalation'
  | 'overtalk_nudge'
  | 'time_warning_while_speaking'
  | 'wrapup_while_speaking'
  | 'overtime_while_speaking';

export interface PersonalPrivateNotificationItemV1 {
  notificationId: string;
  code: PersonalPrivateNotificationCode;
  severity: 'warning' | 'alert';
  category: 'behavior' | 'time_management';
  sourceActionKind: string;
  agendaItemId: string | null;
  title: string;
  message: string;
  createdAtMs: number;
}

export interface PersonalQueueActionStateV1 {
  status: 'open' | 'completed' | 'dismissed' | 'snoozed';
  updatedAtMs: number | null;
  remindUntilMs: number | null;
}

export interface PersonalLiveInsightItemV1 {
  itemId: string;
  code:
    | 'viewer_link_pending'
    | 'close_decision_while_speaking'
    | 'keep_topic_open'
    | 'confirm_weak_coverage'
    | 'recover_at_risk_topic'
    | 'watch_shared_goal_risk';
  priority: LiveMeetingInsightPriority;
  agendaItemId: string | null;
  relatedSharedSignalId: string | null;
  originKind: PersonalLiveInsightOriginKind;
  message: string;
  actionState: PersonalQueueActionStateV1;
}

export interface PersonalLiveToneSignalV1 {
  source: 'deterministic_live_tone_proxy' | 'llm_refined';
  status:
    | 'insufficient_evidence'
    | 'constructive'
    | 'neutral'
    | 'negative'
    | 'contentious'
    | 'argumentative';
  intensity: 'low' | 'medium' | 'high';
  summary: string;
  coachingTip: string | null;
  lastUpdatedAtMs: number | null;
}

export interface PersonalLiveMeetingViewV1 {
  visibility: 'viewer_private';
  viewerParticipantId: string | null;
  viewerDisplayName: string | null;
  viewerRole: PersonalLiveMeetingViewerRole;
  onCall: boolean;
  isCurrentSpeaker: boolean;
  currentStatus: string;
  overallToneSignal: PersonalLiveToneSignalV1 | null;
  viewerToneSignal: PersonalLiveToneSignalV1 | null;
  privateTips: string[];
  privateNotifications: PersonalPrivateNotificationItemV1[];
  memberPrivateGoalSummary: LiveMeetingInsightsLifecycleCounts | null;
  personalQueue: PersonalLiveInsightItemV1[];
}

export interface LiveMeetingInsightsDtoV1 {
  schemaVersion: 1;
  meetingId: string;
  dataMode?: 'live' | 'persisted_snapshot';
  sourceMeetingId?: string;
  sourceMeetingEndedAtMs?: number | null;
  sharedTeamView: SharedTeamLiveMeetingViewV1;
  personalView: PersonalLiveMeetingViewV1;
}

export const LIVE_MEETING_INSIGHTS_REALTIME_KIND = 'LIVE_MEETING_INSIGHTS_REALTIME_V1' as const;
